import { SearchResult, transformSearchResultsToNuggets } from '@backrs_ui/common-ui';

import { formatNuggetVariantName } from '@backrs_ui/grow-ui/src/pages/Search/search.util';
import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

// Define the return type for the autocomplete query
export type ContentSearchAutocompleteResult = Readonly<{
  endpoint: string;
  values: {
    label: string;
    value: string;
    variant?: string;
    title?: string;
    hasRelatedCollegeInfo?: boolean;
  }[];
  searchTerm?: string;
}>;

export const contentSearchApi = createApi({
  reducerPath: 'contentSearchApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['ContentSearch'],
  endpoints: (builder) => ({
    fetchContentSearchAutocompleteOptions: builder.query<
      ContentSearchAutocompleteResult[],
      { searchTerm: string }
    >({
      queryFn: async ({ searchTerm }) => {
        try {
          if (!searchTerm || searchTerm.length < 4) {
            // Full text search has to be at least 4 characters
            return { data: [] };
          }

          const url = new URL(`${ENVIRONMENT_URL}/content-search`);

          const filter = JSON.stringify({
            q: searchTerm,
          });

          const sort = JSON.stringify(['title', 'ASC']);

          url.searchParams.append('filter', filter);
          url.searchParams.append('sort', sort);

          const response = await fetch(url.toString(), {});

          if (!response.ok) {
            throw new Error('Failed to fetch autocomplete options');
          }

          const data: SearchResult = await response.json();

          const results = transformSearchResultsToNuggets(data.results, true).nuggets;

          const values = results.map((nugget) => {
            const variantLabel = formatNuggetVariantName(nugget.variant);

            return {
              label: `${nugget.title} (${variantLabel})`,
              value: nugget.id.toString(),
              variant: nugget.variant,
              title: nugget.title,
              hasRelatedCollegeInfo: nugget?.college_info?.id ? true : false,
            };
          });

          const formattedData = {
            endpoint: '/content-search',
            values,
          };

          return { data: [formattedData] };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },
    }),
  }),
});

export const { useLazyFetchContentSearchAutocompleteOptionsQuery } = contentSearchApi;
